import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User, 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  multiFactor,
  TotpMultiFactorGenerator,
  TotpSecret,
  AuthError,
  getMultiFactorResolver,
  MultiFactorResolver
} from 'firebase/auth';
import { auth } from '../services/firebase';

export interface MFASetupData {
  secret: TotpSecret;
  qrCodeUrl: string;
  backupCodes?: string[];
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  mfaRequired: boolean;
  hasMFA: boolean;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  sendVerificationEmail: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  generateMFASecret: () => Promise<MFASetupData>;
  enrollMFA: (secret: TotpSecret, verificationCode: string) => Promise<void>;
  verifyMFA: (verificationCode: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [pendingResolver, setPendingResolver] = useState<MultiFactorResolver | null>(null);
  const [emulatorMfaEnrolled, setEmulatorMfaEnrolled] = useState(false);

  // Check if we're in development mode (emulator)
  const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';

  // Computed property for MFA status
  const hasMFA = (() => {
    if (!user) return false;
    
  
    if (isDevelopment) {
      // For emulator, use state that updates reactively
      return emulatorMfaEnrolled;
    }
    
    return multiFactor(user).enrolledFactors.length > 0;
  })();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('Auth state changed:', { 
        user: user?.email || 'null', 
        uid: user?.uid,
        emailVerified: user?.emailVerified 
      });
      
      setUser(user);
      setLoading(false);
      setMfaRequired(false); // Reset MFA required state on user change
      
      // Load emulator MFA status from localStorage
      if (user) {
        const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
        console.log('isDevelopment', isDevelopment);
        console.log('process.env.NODE_ENV', process.env.NODE_ENV);
        console.log('process.env', process.env);
        if (isDevelopment) {
          const mfaEnrolled = localStorage.getItem(`mfa_enrolled_${user.uid}`) === 'true';
          console.log(`Loading emulator MFA status for ${user.uid}:`, mfaEnrolled);
          setEmulatorMfaEnrolled(mfaEnrolled);
        }
      } else {
        console.log('No user - clearing emulator MFA status');
        setEmulatorMfaEnrolled(false);
      }
    });

    return unsubscribe;
  }, []);

  const handleAuthError = (error: AuthError) => {
    console.error('Auth error:', error);
    
    switch (error.code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        setError('Invalid email or password');
        break;
      case 'auth/email-already-in-use':
        setError('Email already in use');
        break;
      case 'auth/weak-password':
        setError('Password is too weak');
        break;
      case 'auth/invalid-email':
        setError('Invalid email address');
        break;
      case 'auth/too-many-requests':
        setError('Too many failed attempts. Please try again later.');
        break;
      case 'auth/popup-closed-by-user':
        setError('Sign-in popup was closed');
        break;
      case 'auth/cancelled-popup-request':
        setError('Sign-in was cancelled');
        break;
      case 'auth/multi-factor-auth-required':
        setError('Multi-factor authentication required');
        break;
      case 'auth/invalid-multi-factor-session':
        setError('Invalid multi-factor session');
        break;
      case 'auth/multi-factor-info-not-found':
        setError('Multi-factor authentication not set up');
        break;
      case 'auth/multi-factor-auth-required':
        // This is handled in signInWithEmail
        break;
      default:
        setError('An error occurred during authentication');
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      if (error.code === 'auth/multi-factor-auth-required') {
        // MFA is required, create resolver from error
        const resolver = getMultiFactorResolver(auth, error);
        setPendingResolver(resolver);
        setMfaRequired(true);
        setError(null); // Clear error since this is expected
      } else {
        handleAuthError(error as AuthError);
      }
    } finally {
      setLoading(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Automatically send email verification after signup
      if (userCredential.user) {
        await sendEmailVerification(userCredential.user);
      }
    } catch (error) {
      handleAuthError(error as AuthError);
    } finally {
      setLoading(false);
    }
  };

  const sendVerificationEmail = async () => {
    try {
      setError(null);
      if (user) {
        await sendEmailVerification(user);
      } else {
        setError('No user found. Please sign in first.');
      }
    } catch (error) {
      handleAuthError(error as AuthError);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      handleAuthError(error as AuthError);
    }
  };

  const generateMFASecret = async (): Promise<MFASetupData> => {
    try {
      setError(null);
      if (!user) {
        throw new Error('User must be signed in to set up MFA');
      }

      // Check if we're in development mode (emulator)
      const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
      
      if (isDevelopment) {
        console.warn('MFA is not fully supported in Firebase Auth emulator. Using mock data for development.');
        
        // Return mock data for emulator testing
        return {
          secret: {
            secretKey: 'MOCK_SECRET_KEY_FOR_EMULATOR_TESTING',
            generateQrCodeUrl: (accountName: string, issuer: string) => 
              `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=otpauth://totp/${encodeURIComponent(accountName)}?secret=MOCK_SECRET_KEY_FOR_EMULATOR_TESTING&issuer=${encodeURIComponent(issuer)}`
          } as any,
          qrCodeUrl: `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=otpauth://totp/${encodeURIComponent(user.email || 'user@finstack.dev')}?secret=MOCK_SECRET_KEY_FOR_EMULATOR_TESTING&issuer=${encodeURIComponent('FinStack')}`
        };
      }

      const multiFactorSession = await multiFactor(user).getSession();
      const totpSecret = await TotpMultiFactorGenerator.generateSecret(multiFactorSession);
      
      // Generate QR code URL for authenticator apps
      // generateQrCodeUrl returns an otpauth:// URI, not an image URL
      const totpUri = totpSecret.generateQrCodeUrl(user.email || '', 'FinStack');
      
      // Convert the otpauth URI to an actual QR code image URL
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(totpUri)}`;
      
      return {
        secret: totpSecret,
        qrCodeUrl,
      };
    } catch (error) {
      console.error('MFA Secret Generation Error:', error);
      handleAuthError(error as AuthError);
      throw error;
    }
  };

  const enrollMFA = async (secret: TotpSecret, verificationCode: string) => {
    try {
      setError(null);
      if (!user) {
        throw new Error('User must be signed in to enroll MFA');
      }

      // Check if we're in development mode (emulator)
      const isDevelopment = process.env.REACT_APP_ENVIRONMENT === 'development';
      
      if (isDevelopment) {
        console.warn('MFA enrollment is not fully supported in Firebase Auth emulator. Simulating enrollment for development.');
        
        // For emulator, just validate the code format and simulate success
        if (verificationCode.length !== 6 || !/^\d+$/.test(verificationCode)) {
          throw new Error('Invalid verification code format');
        }
        
        // Simulate successful enrollment by setting localStorage flag and updating state
        localStorage.setItem(`mfa_enrolled_${user.uid}`, 'true');
        setEmulatorMfaEnrolled(true);
        console.log('Simulated MFA enrollment successful for emulator. emulatorMfaEnrolled set to true');
        console.log('Current hasMFA will be:', true);
        return;
      }

      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        verificationCode
      );
      
      await multiFactor(user).enroll(multiFactorAssertion, 'TOTP');
    } catch (error) {
      console.error('MFA Enrollment Error:', error);
      handleAuthError(error as AuthError);
      throw error;
    }
  };

  const verifyMFA = async (verificationCode: string) => {
    try {
      setError(null);
      if (!pendingResolver) {
        throw new Error('No pending MFA challenge');
      }

      // Use the resolver's hints to get the TOTP factor
      const selectedHint = pendingResolver.hints[0]; // Use first enrolled factor (TOTP)
      
      const multiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        selectedHint.uid,
        verificationCode
      );
      
      await pendingResolver.resolveSignIn(multiFactorAssertion);
      
      // Clear MFA state after successful verification
      setMfaRequired(false);
      setPendingResolver(null);
    } catch (error) {
      handleAuthError(error as AuthError);
      throw error;
    }
  };

  const logout = async () => {
    try {
      setError(null);
      
      // Clear emulator MFA state if in emulator mode
      if (user) {
        const isEmulator = process.env.REACT_APP_ENVIRONMENT === 'development';
        if (isEmulator) {
          localStorage.removeItem(`mfa_enrolled_${user.uid}`);
          setEmulatorMfaEnrolled(false);
        }
      }
      
      await signOut(auth);
      // Clear MFA state on logout
      setMfaRequired(false);
      setPendingResolver(null);
    } catch (error) {
      handleAuthError(error as AuthError);
    }
  };

  const clearError = () => {
    setError(null);
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    mfaRequired,
    hasMFA,
    signInWithEmail,
    signUpWithEmail,
    sendVerificationEmail,
    resetPassword,
    generateMFASecret,
    enrollMFA,
    verifyMFA,
    logout,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};